import { Controller } from "@hotwired/stimulus"

/**
 * Generic slide-out drawer. The direction is read from a `data-direction`
 * attribute on the panel target — `"left"` hides via `-translate-x-full`,
 * anything else (or unset) hides via `translate-x-full`. Closes on Escape.
 *
 * For multiple drawers on one page, give each its own `data-controller="slide-panel"`
 * scope (a wrapper div with `class="contents"` keeps the layout flat). Each scope
 * has its own panel target and triggers, so they toggle independently.
 *
 * Connects to data-controller="slide-panel"
 *
 * Usage (left-side):
 *   <div data-controller="slide-panel" class="contents">
 *     <button data-action="click->slide-panel#toggle">Open</button>
 *     <aside data-slide-panel-target="panel"
 *            data-direction="left"
 *            class="absolute left-0 top-0 h-full -translate-x-full transition-transform">
 *       <button data-action="click->slide-panel#close">Close</button>
 *     </aside>
 *   </div>
 */
export default class extends Controller {
  static targets = ["panel"]

  declare readonly panelTarget: HTMLElement

  private boundHandleKeydown!: (event: KeyboardEvent) => void

  connect() {
    this.boundHandleKeydown = this.handleKeydown.bind(this)
    document.addEventListener("keydown", this.boundHandleKeydown)
  }

  disconnect() {
    document.removeEventListener("keydown", this.boundHandleKeydown)
  }

  toggle() {
    if (this.isOpen) {
      this.close()
    } else {
      this.open()
    }
  }

  open() {
    this.panelTarget.classList.remove(this.hiddenClass)
  }

  close() {
    this.panelTarget.classList.add(this.hiddenClass)
  }

  private get isOpen(): boolean {
    return !this.panelTarget.classList.contains(this.hiddenClass)
  }

  private get hiddenClass(): string {
    return this.panelTarget.dataset.direction === "left" ? "-translate-x-full" : "translate-x-full"
  }

  private handleKeydown(event: KeyboardEvent) {
    if (event.key === "Escape") {
      this.close()
    }
  }
}
